// src/components/Codebook/CodebookHistory.tsx
// Display saved/generated codebook tutorials from history

import React, { useState, useEffect } from 'react';
import codebookApi from '../../services/codebookApi';
import { CodebookListItem } from '../../types/chat';
import './CodebookHistory.css';

interface CodebookHistoryProps {
  onSelectCodebook: (codebook: CodebookListItem) => void;
}

const CodebookHistory: React.FC<CodebookHistoryProps> = ({ onSelectCodebook }) => {
  const [codebooks, setCodebooks] = useState<CodebookListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCodebooks();
  }, []);

  const loadCodebooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await codebookApi.listCodebooks(20, 0);
      setCodebooks(response.codebooks);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load bookmates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (codebook_id: string) => {
    if (!window.confirm('Delete this bookmate? This cannot be undone.')) {
      return;
    }

    try {
      await codebookApi.deleteCodebook(codebook_id);
      setCodebooks(codebooks.filter(c => c.codebook_id !== codebook_id));
    } catch (err: any) {
      alert('Failed to delete bookmate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownloadPDF = async (codebook_id: string, title: string) => {
    try {
      const pdf = await codebookApi.downloadPDF(codebook_id);
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('Failed to download PDF: ' + (err.response?.data?.detail || err.message));
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-600';
      case 'generating':
        return 'bg-blue-500/20 text-blue-600';
      case 'failed':
        return 'bg-red-500/20 text-red-600';
      default:
        return 'bg-gray-500/20 text-gray-600';
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const capitalizeTitle = (text: string): string => {
    if (!text) return text;
    // Capitalize first letter of the entire string
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const extractLanguages = (prompt: string): string[] => {
    // Common programming language patterns
    const languages = ['Python', 'Java', 'JavaScript', 'TypeScript', 'C\\+\\+', 'C#', 'Ruby', 'Go', 'Rust', 'Swift', 'Kotlin', 'PHP', 'C', 'SQL', 'R', 'Scala', 'Perl', 'Dart', 'Lua', 'Haskell'];
    const found: string[] = [];

    languages.forEach(lang => {
      const regex = new RegExp(`\\b${lang}\\b`, 'i');
      if (regex.test(prompt)) {
        // Remove escape characters for display
        found.push(lang.replace(/\\/g, ''));
      }
    });

    return found;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading bookmates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="codebook-history h-full flex flex-col">
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-600 p-4 m-4 rounded-lg">
            {error}
          </div>
        )}

        {codebooks.length === 0 ? (
          <div className="flex items-center justify-center h-full text-center">
            <div className="space-y-4">
              <div className="text-6xl">📚</div>
              <p className="text-foreground font-medium">No completed bookmates yet</p>
              <p className="text-muted-foreground text-sm">
                Generate your first interactive programming tutorial
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
            {codebooks.filter(c => c.status === 'completed' && c.total_blocks > 0).map((codebook) => (
              <div
                key={codebook.codebook_id}
                className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-all cursor-pointer group"
                onClick={() => onSelectCodebook(codebook)}
              >
                {/* Header with title and status */}
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-foreground flex-1 line-clamp-2 group-hover:text-primary transition-colors">
                    {capitalizeTitle(codebook.prompt)}
                  </h3>
                  <span className={`text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap ml-2 ${getStatusBadgeColor(codebook.status)}`}>
                    {codebook.status}
                  </span>
                </div>

                {/* Language badges */}
                {extractLanguages(codebook.prompt).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {extractLanguages(codebook.prompt).map((lang) => (
                      <span
                        key={lang}
                        className="text-xs font-medium px-2 py-1 bg-primary/10 text-primary rounded-md"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4 border-t border-border pt-3">
                  <div>
                    <span className="font-medium">{codebook.total_blocks}</span> blocks
                  </div>
                  <div>
                    {codebook.created_at && formatDate(codebook.created_at)}
                  </div>
                </div>

                {/* Actions */}
                {codebook.status === 'completed' && (
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(codebook.codebook_id, codebook.title);
                      }}
                      className="flex-1 px-3 py-2 text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 rounded-md transition-colors"
                    >
                      📥 PDF
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(codebook.codebook_id);
                      }}
                      className="flex-1 px-3 py-2 text-xs font-medium bg-red-500/10 text-red-600 hover:bg-red-500/20 rounded-md transition-colors"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CodebookHistory;
